import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Building2, Search, Users, MapPin,
  Filter, ChevronDown, ChevronUp, Loader2, Briefcase,
  Globe, UserCheck, Wrench, CalendarDays
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GROUP_MODES = [
  { key: "industry", label: "Industry", icon: Building2 },
  { key: "state", label: "State", icon: MapPin },
  { key: "vertical", label: "Vertical", icon: Briefcase },
];

export default function HappyCustomersPage() {
  const [groupedClients, setGroupedClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [expandedGroups, setExpandedGroups] = useState({});
  const [totalClients, setTotalClients] = useState(0);
  const [groupMode, setGroupMode] = useState("industry");

  const fetchClients = async (mode) => {
    setLoading(true);
    try {
      let endpoint = "/clients/by-industry";
      if (mode === "state") endpoint = "/clients/by-state";
      else if (mode === "vertical") endpoint = "/clients/by-vertical-group";

      const res = await api.get(endpoint);
      const data = res.data;

      const normalized = data.map(g => ({
        group_name: g.group_name || g.industry || "Others",
        clients: g.clients,
        count: g.count
      }));

      setGroupedClients(normalized);
      setTotalClients(normalized.reduce((sum, g) => sum + g.count, 0));
      const expanded = {};
      normalized.slice(0, 3).forEach(g => { expanded[g.group_name] = true; });
      setExpandedGroups(expanded);
      setSelectedGroup("all");
    } catch (err) {
      toast.error("Failed to load customer data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClients(groupMode); }, [groupMode]);

  const getLogoUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${API_URL}${url}`;
  };

  const groupNames = groupedClients.map(g => g.group_name);

  const filteredGroups = groupedClients
    .filter(g => selectedGroup === "all" || g.group_name === selectedGroup)
    .map(g => ({
      ...g,
      clients: g.clients.filter(c =>
        searchQuery === "" ||
        c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.industry?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.state?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }))
    .filter(g => g.clients.length > 0);

  const filteredTotal = filteredGroups.reduce((sum, g) => sum + g.clients.length, 0);

  const toggleGroup = (name) => {
    setExpandedGroups(prev => ({ ...prev, [name]: !prev[name] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-[#0066CC]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="happy-customers-page">
      <SEOHead
        title="Our Happy Customers - 80+ Successful ERP Implementations"
        description="See how Absolin has helped 80+ businesses across India with ERP implementation, digital transformation, and custom solutions. Trusted by manufacturing, retail, FMCG, and more."
        keywords="Absolin customers, ERP implementation success stories, Odoo ERP clients India, digital transformation case studies, MSME ERP success"
        canonicalUrl="/our-customers"
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "Our Customers" }]}
      />
      <Navbar />

      <main className="pt-16 md:pt-20">
        {/* Hero */}
        <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-10 md:py-16 relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Ccircle cx=\'30\' cy=\'30\' r=\'2\'/%3E%3C/g%3E%3C/svg%3E")' }} />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/" className="inline-flex items-center text-white/80 hover:text-white text-sm mb-6 transition-colors" data-testid="back-home-link">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Home
            </Link>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                <Users className="h-6 w-6 md:h-7 md:w-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white">
                  Our Happy <span className="text-[#4d9dff]">Customers</span>
                </h1>
                <p className="text-white/70 text-sm md:text-lg mt-1">
                  Trusted by {totalClients}+ businesses across diverse industries
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 mt-4">
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
                <Building2 className="w-3 h-3 mr-1" /> {totalClients} Clients
              </Badge>
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
                {groupNames.length} {groupMode === "industry" ? "Industries" : groupMode === "state" ? "States" : "Verticals"}
              </Badge>
            </div>
          </div>
        </section>

        {/* Filters + Grouping Toggle */}
        <section className="bg-white border-b border-slate-200 sticky top-16 md:top-20 z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 md:py-4">
            <div className="flex flex-col gap-3">
              {/* Group by toggle */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Group by:</span>
                <div className="flex bg-slate-100 rounded-lg p-0.5 gap-0.5" data-testid="group-mode-toggle">
                  {GROUP_MODES.map(m => {
                    const Icon = m.icon;
                    return (
                      <button
                        key={m.key}
                        onClick={() => setGroupMode(m.key)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                          groupMode === m.key
                            ? "bg-white text-[#0066CC] shadow-sm"
                            : "text-slate-500 hover:text-slate-700"
                        }`}
                        data-testid={`group-by-${m.key}`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {m.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Search + Filter */}
              <div className="flex flex-col md:flex-row gap-3 md:gap-4 items-start md:items-center justify-between">
                <div className="relative w-full md:w-80">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search clients by name or location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="customer-search"
                  />
                </div>
                <div className="flex items-center gap-3 w-full md:w-auto">
                  <Filter className="h-4 w-4 text-slate-500 hidden sm:block" />
                  <select
                    value={selectedGroup}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                    className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#0066CC]/20 focus:border-[#0066CC] w-full md:w-auto"
                    data-testid="industry-filter"
                  >
                    <option value="all">All ({totalClients})</option>
                    {groupedClients.map(g => (
                      <option key={g.group_name} value={g.group_name}>{g.group_name} ({g.count})</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Client Groups */}
        <section className="py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
            {filteredGroups.length === 0 ? (
              <div className="text-center py-16">
                <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">No matching customers</h3>
                <p className="text-slate-500">Try adjusting your search or filter</p>
                <Button variant="outline" className="mt-4" onClick={() => { setSearchQuery(""); setSelectedGroup("all"); }}>
                  Clear Filters
                </Button>
              </div>
            ) : (
              filteredGroups.map((group) => (
                <GroupSection
                  key={group.group_name}
                  group={group}
                  expanded={expandedGroups[group.group_name] !== false}
                  onToggle={() => toggleGroup(group.group_name)}
                  getLogoUrl={getLogoUrl}
                  groupMode={groupMode}
                />
              ))
            )}

            {filteredTotal > 0 && (
              <p className="text-center text-slate-500 text-sm mt-6">
                Showing {filteredTotal} of {totalClients} customers
              </p>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}


function GroupSection({ group, expanded, onToggle, getLogoUrl, groupMode }) {
  const IconComp = groupMode === "state" ? MapPin : groupMode === "vertical" ? Briefcase : Building2;
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`group-section-${group.group_name}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#0066CC]/10 flex items-center justify-center flex-shrink-0">
            <IconComp className="w-5 h-5 text-[#0066CC]" />
          </div>
          <div>
            <h2 className="text-base md:text-lg font-bold text-slate-900">{group.group_name}</h2>
            <p className="text-xs text-slate-500">{group.clients.length} client{group.clients.length !== 1 ? "s" : ""}</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 border-t border-slate-100 pt-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {group.clients.map((client) => (
                  <ClientCard key={client.id} client={client} getLogoUrl={getLogoUrl} />
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


function ClientCard({ client, getLogoUrl }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const cardRef = useRef(null);
  const timeoutRef = useRef(null);

  const logoUrl = getLogoUrl(client.logo_url);
  const initials = client.name
    .split(/[\s&]+/)
    .filter(w => w.length > 1)
    .slice(0, 2)
    .map(w => w[0])
    .join("")
    .toUpperCase();

  const hasDetails = client.about || client.state || client.employee_count || (client.services_provided && client.services_provided.length > 0) || client.onboard_date;

  const yearsWithAbsolin = (() => {
    if (!client.onboard_date) return null;
    const onboard = new Date(client.onboard_date);
    const now = new Date();
    const diffYears = (now - onboard) / (365.25 * 24 * 60 * 60 * 1000);
    return Math.ceil(diffYears);
  })();

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect();
        setTooltipPos({
          top: rect.top - 8,
          left: rect.left + rect.width / 2
        });
      }
      setShowTooltip(true);
    }, 300);
  };
  const handleMouseLeave = () => {
    clearTimeout(timeoutRef.current);
    setShowTooltip(false);
  };

  return (
    <div
      ref={cardRef}
      className="group relative flex flex-col items-center gap-2 p-3 rounded-xl border border-slate-100 hover:border-[#0066CC]/30 hover:shadow-md transition-all duration-200 bg-white cursor-default"
      data-testid={`client-card-${client.id}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden group-hover:border-[#0066CC]/20 transition-colors">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={client.name}
            className="w-full h-full object-contain p-1.5"
            onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
          />
        ) : null}
        <span
          className={`text-sm sm:text-base font-bold text-[#0066CC]/70 ${logoUrl ? "hidden" : "flex"}`}
          style={{ display: logoUrl ? "none" : "flex" }}
        >
          {initials}
        </span>
      </div>
      <div className="text-center w-full">
        <p className="text-[10px] sm:text-xs font-semibold text-slate-800 line-clamp-2 leading-tight group-hover:text-[#0066CC] transition-colors">
          {client.name}
        </p>
        {client.state && (
          <p className="text-[9px] sm:text-[10px] text-slate-400 mt-0.5 flex items-center justify-center gap-0.5">
            <MapPin className="w-2.5 h-2.5" />
            {client.state}
          </p>
        )}
      </div>

      {/* Hover Tooltip - rendered via portal to avoid overflow clipping */}
      {showTooltip && hasDetails && createPortal(
        <div
          style={{ position: 'fixed', top: tooltipPos.top, left: tooltipPos.left, transform: 'translate(-50%, -100%)', zIndex: 9999 }}
          onMouseEnter={() => clearTimeout(timeoutRef.current)}
          onMouseLeave={handleMouseLeave}
          data-testid={`client-tooltip-${client.id}`}
        >
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.15 }}
            className="w-72"
          >
            <div className="bg-slate-900 text-white rounded-xl shadow-2xl border border-slate-700/50 overflow-hidden">
              {/* Header */}
              <div className="px-4 pt-3 pb-2 border-b border-slate-700/50">
                <p className="font-semibold text-sm text-white truncate">{client.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {client.industry && (
                    <p className="text-[11px] text-slate-400">{client.industry}</p>
                  )}
                  {yearsWithAbsolin && yearsWithAbsolin > 0 && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-500/20 text-emerald-300 rounded text-[10px] font-semibold whitespace-nowrap">
                      <CalendarDays className="w-2.5 h-2.5" />
                      {yearsWithAbsolin} {yearsWithAbsolin === 1 ? 'Year' : 'Years'} Journey
                    </span>
                  )}
                </div>
              </div>

              <div className="px-4 py-2.5 space-y-2">
                {/* About */}
                {client.about && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-0.5">About</p>
                    <p className="text-xs text-slate-300 leading-relaxed line-clamp-3">{client.about}</p>
                  </div>
                )}

                {/* Details Row */}
                <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                  {client.state && (
                    <div className="flex items-center gap-1.5">
                      <MapPin className="w-3 h-3 text-[#4d9dff]" />
                      <span className="text-xs text-slate-300">{client.state}</span>
                    </div>
                  )}
                  {client.employee_count && (
                    <div className="flex items-center gap-1.5">
                      <UserCheck className="w-3 h-3 text-emerald-400" />
                      <span className="text-xs text-slate-300">{client.employee_count} employees</span>
                    </div>
                  )}
                </div>

                {/* Services Provided */}
                {client.services_provided && client.services_provided.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1 flex items-center gap-1">
                      <Wrench className="w-2.5 h-2.5" /> Services
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {client.services_provided.map((svc, i) => (
                        <span key={i} className="px-1.5 py-0.5 bg-[#0066CC]/20 text-[#4d9dff] rounded text-[10px] font-medium">
                          {svc}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Website */}
                {client.website && (
                  <div className="flex items-center gap-1.5">
                    <Globe className="w-3 h-3 text-slate-500" />
                    <a href={client.website} target="_blank" rel="noopener noreferrer" className="text-xs text-[#4d9dff] hover:underline truncate">
                      {client.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                    </a>
                  </div>
                )}
              </div>

              {/* Arrow */}
              <div className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-3 h-3 bg-slate-900 rotate-45 border-r border-b border-slate-700/50" />
            </div>
          </motion.div>
        </div>,
        document.body
      )}
    </div>
  );
}
